import { useId } from 'react'
import { SegmentedControl } from '@/components/SegmentedControl'
import {
  SegmentedIndicator,
  segmentedOptionClassName,
} from '@/components/SegmentedIndicator'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { PerformanceUrlSearch } from '@/lib/performance.types'
import { targetMarketIdsEqual, targetMarketIdsKey } from '@/lib/targetMarkets'
import type { PerformanceFiltersProps } from './PerformanceFilters.types'

const SEARCH_OPTIONS: { value: PerformanceUrlSearch; label: string }[] = [
  { value: 'standard', label: 'Standard' },
  { value: 'live', label: 'Live' },
]

function targetMarketValue(ids: string[]): string {
  return `target:${targetMarketIdsKey(ids)}`
}

export function PerformanceFilters({
  selection,
  brands,
  categories,
  markets,
  targetMarkets,
  onBrandChange,
  onCategoryChange,
  onMarketChange,
  onTargetMarketChange,
  onSearchChange,
}: PerformanceFiltersProps) {
  const searchGroupId = useId()
  const currentMarket = selection.targetMarketIds
    ? targetMarkets.find((market) =>
        targetMarketIdsEqual(
          market.targetMarketIds,
          selection.targetMarketIds ?? []
        )
      )
    : markets.find((market) => market.value === selection.market)
  const showSearchSelector = (currentMarket?.searchTypes.length ?? 0) > 1
  const selectedValue = selection.targetMarketIds
    ? targetMarketValue(selection.targetMarketIds)
    : `market:${selection.market}`

  return (
    <fieldset className="flex flex-wrap items-end gap-4">
      <legend className="sr-only">Performance filters</legend>

      <div className="space-y-2">
        <Label htmlFor="performance-brand">Brand</Label>
        <Select value={selection.brandId} onValueChange={onBrandChange}>
          <SelectTrigger id="performance-brand" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {brands.map((brand) => (
              <SelectItem key={brand.value} value={brand.value}>
                {brand.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="performance-category">Category</Label>
        <Select value={selection.category} onValueChange={onCategoryChange}>
          <SelectTrigger id="performance-category" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categories.map((category) => (
              <SelectItem key={category.value} value={category.value}>
                {category.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="performance-market">Market</Label>
        <Select
          value={selectedValue}
          onValueChange={(value) => {
            if (value.startsWith('target:')) {
              onTargetMarketChange(value.slice('target:'.length).split('|'))

              return
            }

            onMarketChange(value.slice('market:'.length))
          }}
        >
          <SelectTrigger id="performance-market" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {markets.map((market) => (
              <SelectItem
                key={`market:${market.value}`}
                value={`market:${market.value}`}
              >
                {market.label}
              </SelectItem>
            ))}
            {targetMarkets.map((market) => (
              <SelectItem
                key={targetMarketValue(market.targetMarketIds)}
                value={targetMarketValue(market.targetMarketIds)}
              >
                {market.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {showSearchSelector && (
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-muted-foreground">
            Search type
          </legend>
          <SegmentedControl>
            {SEARCH_OPTIONS.map((option) => {
              const active = selection.search === option.value

              return (
                <label
                  key={option.value}
                  className={segmentedOptionClassName(active)}
                >
                  {active && <SegmentedIndicator layoutId={searchGroupId} />}
                  <input
                    type="radio"
                    name={searchGroupId}
                    value={option.value}
                    checked={active}
                    onChange={() => onSearchChange(option.value)}
                    className="sr-only"
                  />
                  <span className="relative">{option.label}</span>
                </label>
              )
            })}
          </SegmentedControl>
        </fieldset>
      )}
    </fieldset>
  )
}
